import { v4 as uuid } from 'uuid';

import { LinkLabelCategory } from 'src/containers/ProjectDiagram/constants/categories';
import {
  Mapping,
  MappingIdentifier,
  MappingLabel,
  ModelConceptRelationship,
  RelationshipMapping,
  Resource,
} from 'src/containers/ProjectDiagram/types/data';
import { LinkData } from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { areIdentifiersEqual } from 'src/containers/ProjectDiagram/utils/mapping/areIdentifiersEqual';
import { createMapping } from 'src/containers/ProjectDiagram/utils/mapping/createMapping';
import { createMappingLabel } from 'src/containers/ProjectDiagram/utils/mapping/createMappingLabel';
import { createRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/createRelationshipMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getLabelKeyFromRelationshipKey } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationshipLabel';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { Project, ProjectsState } from 'src/store/state/projects';
import { ReduxState } from 'src/store/types';

export interface OldRelationshipMapping extends Mapping {
  sourcePrimaryIdentifier: MappingIdentifier;
  targetPrimaryIdentifier: MappingIdentifier;
}

const getMappingsWithPrimaryIdentifier = (
  mappings: Mapping[],
  classKey: string,
  primaryIdentifier: MappingIdentifier
) =>
  mappings.filter(
    (mapping) =>
      mapping.to === classKey &&
      areIdentifiersEqual(primaryIdentifier, mapping.primaryIdentifier)
  );

const createNewMappingsBasedOnOldMappings = ({
  labelsByIri,
  oldNonRelationshipMappings,
  relationshipData,
  relationshipMapping,
  resourceName,
}: {
  labelsByIri: Record<string, string>;
  oldNonRelationshipMappings: Mapping[];
  relationshipData: ModelConceptRelationship;
  relationshipMapping: OldRelationshipMapping;
  resourceName: string;
}) => {
  if (!relationshipData.from || !relationshipData.to) {
    return {
      classMappings: [] as Mapping[],
      relationshipMappings: [] as RelationshipMapping[],
    };
  }

  const classMappingsThatUseSourcePrimaryIdentifier: Mapping[] = [];
  const classMappingsThatUseTargetPrimaryIdentifier: Mapping[] = [];
  if (
    relationshipMapping.sourcePrimaryIdentifier.parts.length &&
    relationshipData.from
  ) {
    classMappingsThatUseSourcePrimaryIdentifier.push(
      ...getMappingsWithPrimaryIdentifier(
        oldNonRelationshipMappings,
        relationshipData.from,
        relationshipMapping.sourcePrimaryIdentifier
      )
    );
  }
  if (
    relationshipMapping.targetPrimaryIdentifier.parts.length &&
    relationshipData.to
  ) {
    classMappingsThatUseTargetPrimaryIdentifier.push(
      ...getMappingsWithPrimaryIdentifier(
        oldNonRelationshipMappings,
        relationshipData.to,
        relationshipMapping.targetPrimaryIdentifier
      )
    );
  }

  const fromId = getResourceValue(relationshipMapping.from);
  const newClassMappings: Mapping[] = [];

  let sourceMapping: Mapping;
  let targetMapping: Mapping;
  if (classMappingsThatUseSourcePrimaryIdentifier.length !== 1) {
    const newMappingName =
      classMappingsThatUseSourcePrimaryIdentifier.length > 1
        ? `[${resourceName}]-[${getLabelForIri(
            labelsByIri,
            getModelConceptClassValue(relationshipData.from)
          )}]`
        : relationshipMapping.text;
    const newSourceMapping = createMapping({
      fromId,
      primaryIdentifier: relationshipMapping.sourcePrimaryIdentifier,
      text: newMappingName,
      toKey: relationshipData.from,
      value: uuid(),
    });
    newClassMappings.push(newSourceMapping);
    sourceMapping = newSourceMapping;
  } else {
    [sourceMapping] = classMappingsThatUseSourcePrimaryIdentifier;
  }

  if (classMappingsThatUseTargetPrimaryIdentifier.length !== 1) {
    const newMappingName =
      classMappingsThatUseTargetPrimaryIdentifier.length > 1
        ? `[${resourceName}]-[${getLabelForIri(
            labelsByIri,
            getModelConceptClassValue(relationshipData.to)
          )}]`
        : relationshipMapping.text;
    const newTargetMapping = createMapping({
      fromId,
      primaryIdentifier: relationshipMapping.targetPrimaryIdentifier,
      text: newMappingName,
      toKey: relationshipData.to,
      value: uuid(),
    });
    newClassMappings.push(newTargetMapping);
    targetMapping = newTargetMapping;
  } else {
    [targetMapping] = classMappingsThatUseTargetPrimaryIdentifier;
  }

  // @ts-ignore
  const newRelationshipMapping = createRelationshipMapping({
    from: sourceMapping.key,
    relationshipIri: relationshipData.value,
    to: targetMapping.key,
  });

  return {
    classMappings: newClassMappings,
    relationshipMappings: [newRelationshipMapping],
  };
};

/**
 * Drops existing relationships mappings and labels and creates new class mappings and mapping labels
 * based on criteria defined in VET-3153.
 */
// prevState must be typed as `any` since it's an older version of the Redux State

export const v18 = (prevState: any): ReduxState => {
  const nextState = { ...prevState };

  if (nextState.projects) {
    nextState.projects = v18Projects(prevState.projects);
  }

  return nextState;
};

const v18Projects = (prevProjectsState: any): ProjectsState => {
  const projectsById: Record<string, Project> = {};

  Object.entries(
    prevProjectsState.projectsById as Record<string, Project>
  ).forEach(([projectId, project]) => {
    const { diagram } = project;
    if (!diagram) {
      projectsById[projectId] = project;
      return;
    }

    const { labelsByIri } = diagram.model.modelData;
    const keptMappings: Mapping[] = []; // class & unconnected mappings
    const otherKeptLinks: LinkData[] = [];
    const relationshipMappingLinks: OldRelationshipMapping[] = [];
    const newlyCreatedMappings: Array<Mapping | RelationshipMapping> = [];
    const labelKeysToRemove = new Set<string>();

    diagram.model.linkDataArray.forEach((linkData) => {
      if (
        isMapping(linkData) &&
        linkData.to?.includes(
          LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL
        )
      ) {
        relationshipMappingLinks.push(linkData as OldRelationshipMapping);
      } else if (isMapping(linkData)) {
        keptMappings.push(linkData);
      } else {
        otherKeptLinks.push(linkData);
      }
    });

    relationshipMappingLinks.forEach((relationshipMapping) => {
      if (relationshipMapping.labelKeys.length) {
        relationshipMapping.labelKeys.forEach((labelKey) =>
          labelKeysToRemove.add(labelKey)
        );
      }

      const relationshipData = otherKeptLinks.find(
        (linkData): linkData is ModelConceptRelationship =>
          isModelConceptRelationship(linkData) &&
          relationshipMapping.to ===
            getLabelKeyFromRelationshipKey(linkData.key)
      );

      if (!relationshipData) {
        return;
      }

      const resource = diagram.model.nodeDataArray.find(
        (nodeData): nodeData is Resource =>
          nodeData.key === relationshipMapping.from
      );

      const resourceName = resource?.text || '';

      const { classMappings, relationshipMappings } =
        createNewMappingsBasedOnOldMappings({
          labelsByIri,
          oldNonRelationshipMappings: keptMappings,
          relationshipData,
          relationshipMapping,
          resourceName,
        });
      newlyCreatedMappings.push(...classMappings, ...relationshipMappings);
    });

    // create link labels for newly created class mappings
    const newlyCreatedMappingLabels: MappingLabel[] = [];
    newlyCreatedMappings.forEach((newMapping) => {
      if (isMapping(newMapping)) {
        const mappingLabel = createMappingLabel({
          fromId: getResourceValue(newMapping.from),
          toKey: newMapping.to as string,
          text: newMapping.text,
          value: newMapping.value,
        });
        // eslint-disable-next-line no-param-reassign
        newMapping.labelKeys = [mappingLabel.key];
        newlyCreatedMappingLabels.push(mappingLabel);
      }
    });

    const updatedLinkDataArray = [
      ...otherKeptLinks,
      ...keptMappings,
      ...newlyCreatedMappings,
    ];

    const updatedNodeDataArray = [
      ...diagram.model.nodeDataArray.filter(
        (nodeData) => !labelKeysToRemove.has(nodeData.key)
      ),
      ...newlyCreatedMappingLabels,
    ];

    projectsById[projectId] = {
      ...project,
      diagram: {
        ...diagram,
        model: {
          ...diagram.model,
          linkDataArray: updatedLinkDataArray,
          nodeDataArray: updatedNodeDataArray,
        },
      },
    };
  });

  return { ...prevProjectsState, projectsById };
};
